import React, { useState, useMemo, useCallback } from "react";
import { useHistory } from "react-router-dom";
import Chart from "react-apexcharts";
import { motion } from "framer-motion";
import { FiTrendingUp, FiShoppingBag, FiClock, FiCheckCircle } from "react-icons/fi";
import { toast } from "react-toastify";

import KPICard, { DashboardCard } from "../../Common/KPICard";
import SelectFilter from "../../Common/SelectFilter";
import DateRangeFilter from "../../../Common/DateFilter";
import ActionButton from "../../Common/ActionButton";
import StatusChip from "../../Common/StatusChip";
import "../../../dashboardLayout.css";
import useReportData from "../useReportData";
import { pkrShort, pkrFull, withBars } from "../dashboardFormat";

const theme = {
  primary: "#0b6462",
  primaryLight: "#eefbfb",
  secondary: "#64748b",
  success: "#10b981",
  danger: "#ef4444",
  warning: "#f59e0b",
  info: "#38bdf8",
  textPrimary: "#1e293b",
  textSecondary: "#64748b",
  border: "#e2e8f0",
  surface: "#ffffff",
  shadow: "0 4px 6px -1px rgb(0 0 0 / 0.05), 0 2px 4px -2px rgb(0 0 0 / 0.05)",
  shadowHover: "0 20px 25px -5px rgb(0 0 0 / 0.1), 0 8px 10px -6px rgb(0 0 0 / 0.1)",
};

const containerVariants = {
  hidden: { opacity: 0 },
  show: { opacity: 1, transition: { staggerChildren: 0.05 } },
};

const itemVariants = {
  hidden: { y: 15, opacity: 0 },
  show: { y: 0, opacity: 1, transition: { type: "spring", stiffness: 110 } },
};

const EMPTY_DASH = {
  projects: [],
  kpis: {},
  categories: [],
  poStatus: [],
  aging: [],
  recentPos: [],
};
const CATEGORY_COLORS = ["#22c55e", "#38bdf8", "#f59e0b", "#f87171", "#a855f7", "#14b8a6"];
const AGING_COLORS = ["#22c55e", "#38bdf8", "#f59e0b", "#f87171"];
const PO_STATUS_TYPE = {
  "Fully Received & Closed": "success",
  "In-Transit": "info",
  "Awaiting Approval": "warning",
};

const ProcurementDashboard = () => {
  const history = useHistory();
  const [selectedProject, setSelectedProject] = useState("");
  const [filterDates, setFilterDates] = useState({ from: "", to: "" });
  const params = useMemo(
    () => ({
      projectId: selectedProject || undefined,
      from: filterDates.from || undefined,
      to: filterDates.to || undefined,
    }),
    [selectedProject, filterDates],
  );
  const [dash] = useReportData("ProcurementDashboard", EMPTY_DASH, params);
  const MATERIAL_CATEGORIES = useMemo(() => withBars(dash.categories, CATEGORY_COLORS), [dash]);
  const VENDOR_AGING = useMemo(() => withBars(dash.aging, AGING_COLORS), [dash]);
  const RECENT_PURCHASE_ORDERS = useMemo(
    () =>
      dash.recentPos.map((po) => ({
        ...po,
        amount: pkrFull(po.amount),
        statusType: PO_STATUS_TYPE[po.status] || "default",
      })),
    [dash],
  );
  const totalPos = dash.poStatus.reduce((sum, st) => sum + st.count, 0);

  const kpis = [
    {
      title: "Total Procurement Volume",
      value: pkrShort(dash.kpis.volume),
      subtext: "Approved and completed orders",
      subtextColor: "#64748b",
      icon: FiTrendingUp,
      color: "#0b6462",
      onClick: null,
    },
    {
      title: "Active Purchase Orders (POs)",
      value: String(dash.kpis.activePoCount ?? 0),
      subtext: `Valued at ${pkrShort(dash.kpis.activePoValue)}, awaiting approval or delivery`,
      subtextColor: "#64748b",
      icon: FiShoppingBag,
      color: "#0284c7",
      onClick: () => history.push("/procurement/purchase-order"),
    },
    {
      title: "Pending Requisitions (PRs)",
      value: String(dash.kpis.pendingPrCount ?? 0),
      subtext: "Requires review or approval",
      subtextColor: "#64748b",
      icon: FiClock,
      color: "#f59e0b",
      onClick: () => history.push("/procurement/demand-note"),
    },
    {
      title: "Materials Received (GRNs)",
      value: String(dash.kpis.grnCount ?? 0),
      subtext: "Cumulative goods receipts at site stores",
      subtextColor: "#64748b",
      icon: FiCheckCircle,
      color: "#10b981",
      onClick: () => history.push("/procurement/GRN"),
    },
  ];

  // Donut chart configuration for Purchase Order (PO) Status
  const donutChartOptions = useMemo(
    () => ({
      chart: {
        type: "donut",
        fontFamily: "inherit",
        toolbar: { show: false },
      },
      labels: dash.poStatus.map((st) => st.label),
      colors: ["#0f4c5c", "#0284c7", "#f59e0b"],
      legend: {
        position: "bottom",
        horizontalAlign: "center",
        fontSize: "12px",
        markers: { radius: 12 },
        itemMargin: { horizontal: 10, vertical: 4 },
      },
      dataLabels: {
        enabled: true,
        formatter: (val) => `${val.toFixed(1)}%`,
        style: {
          fontSize: "12px",
          fontWeight: "700",
          colors: ["#ffffff"],
        },
        dropShadow: { enabled: false },
      },
      stroke: {
        width: 2,
        colors: ["#ffffff"],
      },
      plotOptions: {
        pie: {
          donut: {
            size: "62%",
            labels: {
              show: true,
              total: {
                show: true,
                label: "Total POs",
                fontSize: "13px",
                fontWeight: 600,
                color: "#64748b",
                formatter: () => `${totalPos} POs`,
              },
            },
          },
        },
      },
      tooltip: {
        theme: "light",
        y: {
          formatter: (val) => `${val}% of Total Orders`,
        },
      },
    }),
    [dash, totalPos],
  );

  const donutChartSeries = dash.poStatus.map((st) =>
    totalPos ? Math.round((st.count / totalPos) * 1000) / 10 : 0,
  );

  const handleDateRangeChange = useCallback((range) => {
    setFilterDates(range);
  }, []);

  const handleGenerateReport = () => {
    const rangeText =
      filterDates.from && filterDates.to ? ` for ${filterDates.from} to ${filterDates.to}` : "";
    toast.info(`Generating Procurement Summary Report${rangeText}...`);
  };

  return (
    <div className="pt-2 app_font pb-5">
      {/* ── Filter Toolbar ── */}
      <div
        className="d-flex flex-wrap align-items-center justify-content-between mb-3 app_font"
        style={{ gap: "12px" }}
      >
        <div>
          <h4
            style={{
              margin: 0,
              fontSize: "1.25rem",
              fontWeight: 700,
              color: "#0f172a",
            }}
          >
            Procurement Module Dashboard
          </h4>
        </div>

        <div className="d-flex flex-wrap align-items-center" style={{ gap: "10px" }}>
          <SelectFilter
            id="procurementProjectFilter"
            value={selectedProject}
            onChange={(e) => setSelectedProject(e.target.value)}
            label=""
            defaultOptionLabel="All Projects & Sites"
            maxWidth="240px"
            options={dash.projects.map((p) => ({ value: p.id, label: p.name }))}
          />

          <div className="d-flex align-items-center" style={{ width: "230px" }}>
            <div className="floating-field" style={{ width: "100%" }}>
              <DateRangeFilter onChange={handleDateRangeChange} />
              <label>Date Range</label>
            </div>
          </div>

          <ActionButton text="Generate Report" variant="save" onClick={handleGenerateReport} />
        </div>
      </div>

      <motion.div
        variants={containerVariants}
        initial="hidden"
        animate="show"
        className="erp-dashboard-wrap"
      >
        {/* ── Row 1: KPI Summary Cards ── */}
        <div className="erp-kpi-grid">
          {kpis.map((kpi, idx) => (
            <div
              key={idx}
              onClick={kpi.onClick}
              style={{ cursor: kpi.onClick ? "pointer" : "default" }}
            >
              <KPICard
                title={kpi.title}
                value={kpi.value}
                subtitle={kpi.subtext}
                icon={kpi.icon}
                themeColor={kpi.color}
                themeBgColor={`${kpi.color}15`}
                subtitleColor={kpi.subtextColor}
                itemVariants={itemVariants}
              />
            </div>
          ))}
        </div>

        {/* ── Row 2: Procurement Volume by Category & PO Status Donut ── */}
        <div className="erp-two-col-grid">
          {/* Left: Material Category Spend */}
          <DashboardCard
            title="Procurement Volume by Material Category"
            subtitle="Material consumption spend breakdown across projects"
            theme={theme}
            itemVariants={itemVariants}
          >
            <div
              className="d-flex flex-column justify-content-around h-100 py-2"
              style={{ gap: "16px", minHeight: "280px" }}
            >
              {MATERIAL_CATEGORIES.map((item, idx) => {
                const pct = Math.round((item.value / item.max) * 100);
                return (
                  <div key={idx} className="d-flex align-items-center">
                    <div
                      style={{
                        width: "180px",
                        fontSize: "12.5px",
                        fontWeight: 600,
                        color: "#334155",
                        textAlign: "right",
                        paddingRight: "14px",
                        whiteSpace: "nowrap",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                      }}
                      title={item.name}
                    >
                      {item.name}
                    </div>
                    <div
                      className="flex-grow-1"
                      style={{
                        height: "22px",
                        background: "#f1f5f9",
                        borderRadius: "6px",
                        overflow: "hidden",
                      }}
                    >
                      <motion.div
                        initial={{ width: 0 }}
                        animate={{ width: `${pct}%` }}
                        transition={{ duration: 0.8, delay: idx * 0.1 }}
                        style={{
                          height: "100%",
                          background: item.color,
                          borderRadius: "6px",
                        }}
                      />
                    </div>
                    <div
                      style={{
                        width: "95px",
                        fontSize: "12.5px",
                        fontWeight: 700,
                        color: "#0f172a",
                        paddingLeft: "14px",
                        textAlign: "right",
                      }}
                    >
                      {item.amount}
                    </div>
                  </div>
                );
              })}
            </div>
          </DashboardCard>

          {/* Right: Purchase Order (PO) Status */}
          <DashboardCard
            title="Purchase Order (PO) Status"
            subtitle="Fulfillment lifecycle distribution across active orders"
            theme={theme}
            itemVariants={itemVariants}
          >
            <div
              style={{
                minHeight: "280px",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <Chart
                key={donutChartSeries.join("-")}
                options={donutChartOptions}
                series={donutChartSeries}
                type="donut"
                height={280}
                width="100%"
              />
            </div>
          </DashboardCard>
        </div>

        {/* ── Row 3: Vendor Payment Aging & Recent Purchase Orders ── */}
        <div className="erp-two-col-grid erp-mb-0">
          {/* Left: Vendor Payment Aging & Overdue */}
          <DashboardCard
            title="Vendor Payment Aging & Overdue"
            subtitle="Outstanding payable commitments across aging buckets"
            theme={theme}
            itemVariants={itemVariants}
          >
            <div
              className="d-flex flex-column justify-content-around h-100 py-2"
              style={{ gap: "18px", minHeight: "260px" }}
            >
              {VENDOR_AGING.map((item, idx) => {
                const pct = Math.round((item.value / item.max) * 100);
                return (
                  <div key={idx} className="d-flex align-items-center">
                    <div
                      style={{
                        width: "120px",
                        fontSize: "12.5px",
                        fontWeight: 600,
                        color: "#334155",
                        textAlign: "right",
                        paddingRight: "14px",
                      }}
                    >
                      {item.bracket}
                    </div>
                    <div
                      className="flex-grow-1"
                      style={{
                        height: "22px",
                        background: "#f1f5f9",
                        borderRadius: "6px",
                        overflow: "hidden",
                      }}
                    >
                      <motion.div
                        initial={{ width: 0 }}
                        animate={{ width: `${pct}%` }}
                        transition={{ duration: 0.8, delay: idx * 0.1 }}
                        style={{
                          height: "100%",
                          background: item.color,
                          borderRadius: "6px",
                        }}
                      />
                    </div>
                    <div
                      style={{
                        width: "95px",
                        fontSize: "12.5px",
                        fontWeight: 700,
                        color: "#0f172a",
                        paddingLeft: "14px",
                        textAlign: "right",
                      }}
                    >
                      {item.amount}
                    </div>
                  </div>
                );
              })}
            </div>
          </DashboardCard>

          {/* Right: Recent Purchase Orders (PO) Table */}
          <DashboardCard
            title="Recent Purchase Orders (PO)"
            subtitle="Latest issued procurement orders across project sites"
            theme={theme}
            itemVariants={itemVariants}
            action={
              <button
                type="button"
                className="btn btn-sm btn-link p-0"
                style={{
                  fontSize: "12px",
                  color: "var(--color-primary, #0b6462)",
                  fontWeight: 600,
                }}
                onClick={() => history.push("/procurement/purchase-order")}
              >
                View All POs
              </button>
            }
          >
            <div className="table-responsive" style={{ minHeight: "240px" }}>
              <table className="table table-hover align-middle mb-0" style={{ fontSize: "12.5px" }}>
                <thead>
                  <tr
                    style={{
                      background: "#f8fafc",
                      color: "#64748b",
                      borderBottom: "1px solid #e2e8f0",
                    }}
                  >
                    <th style={{ padding: "8px 10px", fontWeight: 600 }}>PO No.</th>
                    <th style={{ padding: "8px 10px", fontWeight: 600 }}>Vendor</th>
                    <th style={{ padding: "8px 10px", fontWeight: 600 }}>Project / Site</th>
                    <th style={{ padding: "8px 10px", fontWeight: 600 }}>Items</th>
                    <th style={{ padding: "8px 10px", fontWeight: 600 }}>Total Amount</th>
                    <th style={{ padding: "8px 10px", fontWeight: 600, textAlign: "center" }}>
                      Status
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {RECENT_PURCHASE_ORDERS.map((po) => (
                    <tr
                      key={po.id}
                      style={{ cursor: "pointer", borderBottom: "1px solid #f1f5f9" }}
                      onClick={() => history.push("/procurement/purchase-order")}
                    >
                      <td
                        style={{
                          padding: "10px",
                          fontWeight: 600,
                          color: "var(--color-primary, #0b6462)",
                        }}
                      >
                        {po.poNo}
                      </td>
                      <td style={{ padding: "10px", fontWeight: 500, color: "#1e293b" }}>
                        {po.vendor}
                      </td>
                      <td style={{ padding: "10px", color: "#64748b" }}>{po.project}</td>
                      <td style={{ padding: "10px", color: "#475569" }}>{po.items}</td>
                      <td style={{ padding: "10px", fontWeight: 600, color: "#0f172a" }}>
                        {po.amount}
                      </td>
                      <td style={{ padding: "10px", textAlign: "center" }}>
                        <StatusChip status={po.status} type={po.statusType} />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </DashboardCard>
        </div>
      </motion.div>
    </div>
  );
};

export default ProcurementDashboard;
